import {
    CustomListItemId,
    PlaybookId,
    PlaybookTouchpointId,
    RegionId,
    serializeActivityTarget,
} from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { inferProcedureOutput } from "@trpc/server";
import { TierTouchpointsEditor } from "components/manage/agency-settings/tier-touchpoints-editor";
import {
    EditableTouchpoint,
    groupTouchpointsByTier,
} from "components/manage/agency-settings/touchpoint-cadence";
import { trpc } from "components/rpc/trpc";
import { ActivityTargetChips } from "components/tasks/activity-chip";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import { Spinner } from "components/ui/spinner";
import React from "react";
import { useFeature, useNow, useRegionId } from "store/selectors/hooks";
import { handleEvent } from "util/user-events";
import { currentYearMonth, monthElapsedFraction } from "../marketing-goals/pace";
import { describeCadence } from "./marketing-plan-format";
import { formatTierVolume, scaleTierVolumeGroups } from "./plan-projection-summary";
import { useAgendaRefresh } from "./use-agenda-refresh";

type Tier = inferProcedureOutput<AppRouter["referralTier"]["tiers"]>[number];
type Touchpoint = inferProcedureOutput<
    AppRouter["marketingPlaybook"]["myPlaybook"]
>["touchpoints"][number];

const TouchpointSummary: React.FC<{ touchpoint: Touchpoint }> = ({ touchpoint }) => (
    <span className="inline-flex flex-wrap items-center gap-1">
        <ActivityTargetChips targets={touchpoint.targets} />
        {describeCadence(touchpoint.intervalCount, touchpoint.intervalUnit)}
    </span>
);

export const MyMarketingPlanEditor: React.FC<unknown> = () => {
    const trpcClient = trpc.useTRPC();
    const marketingPlaybookEnabled = useFeature("marketingPlaybook");
    const regionId = useRegionId();
    const [newRows, setNewRows] = React.useState<
        { key: string; tierListItemId: CustomListItemId; initial: EditableTouchpoint }[]
    >([]);
    const newRowCounter = React.useRef(0);

    const now = useNow();
    const month = currentYearMonth(now);
    const remainingFraction = 1 - monthElapsedFraction(month, now);

    const tiersQuery = useQuery({
        ...trpcClient.referralTier.tiers.queryOptions({
            regionId: regionId ?? ("" as RegionId),
        }),
        enabled: !!regionId,
    });
    const myPlaybookQuery = useQuery({
        ...trpcClient.marketingPlaybook.myPlaybook.queryOptions({
            regionId: regionId ?? ("" as RegionId),
        }),
        enabled: marketingPlaybookEnabled && !!regionId,
    });
    const projectionQuery = useQuery({
        ...trpcClient.marketingPlaybook.myTouchpointVolumeProjection.queryOptions({
            regionId: regionId ?? ("" as RegionId),
            month,
        }),
        enabled: marketingPlaybookEnabled && !!regionId,
    });

    const refreshAgenda = useAgendaRefresh();

    const refetch = () => {
        myPlaybookQuery.refetch();
        projectionQuery.refetch();
        refreshAgenda();
    };

    const customize = useMutation({
        ...trpcClient.marketingPlaybook.customizePlaybook.mutationOptions(),
        onSuccess: refetch,
    });
    const reset = useMutation({
        ...trpcClient.marketingPlaybook.resetPlaybook.mutationOptions(),
        onSuccess: () => {
            setNewRows([]);
            refetch();
        },
    });
    const createTouchpoint = useMutation({
        ...trpcClient.marketingPlaybook.createTouchpoint.mutationOptions(),
        onSuccess: refetch,
    });
    const updateTouchpoint = useMutation({
        ...trpcClient.marketingPlaybook.updateTouchpoint.mutationOptions(),
        onSuccess: refetch,
    });
    const deleteTouchpoint = useMutation({
        ...trpcClient.marketingPlaybook.deleteTouchpoint.mutationOptions(),
        onSuccess: refetch,
    });

    if (!marketingPlaybookEnabled || !regionId) {
        return null;
    }

    const view = myPlaybookQuery.data;
    const isPersonal = view?.isPersonal ?? false;
    const hasAgencyPlaybook = view?.hasAgencyPlaybook ?? false;
    const playbookId = view?.playbookId as PlaybookId | undefined;
    const tiers: Tier[] = (tiersQuery.data ?? []).filter(tier => !tier.isArchived);
    const touchpointsByTier = groupTouchpointsByTier(view?.touchpoints ?? []);
    const projectionByTier = new Map(
        (projectionQuery.data?.tiers ?? []).map(tier => [tier.tierListItemId, tier]),
    );

    const addRow = (tierListItemId: CustomListItemId, initial: EditableTouchpoint) => {
        newRowCounter.current += 1;
        setNewRows(prev => [
            ...prev,
            { key: `new-${newRowCounter.current}`, tierListItemId, initial },
        ]);
    };

    const cancelNewRow = (key: string) => {
        setNewRows(prev => prev.filter(row => row.key !== key));
    };

    const createTouchpointFromRow = (key: string, value: EditableTouchpoint) => {
        const row = newRows.find(r => r.key === key);
        if (!row || !playbookId || value.targets.length === 0) {
            return;
        }
        handleEvent("marketing-plan-touchpoint-create");
        createTouchpoint.mutate(
            {
                regionId,
                playbookId,
                tierListItemId: row.tierListItemId,
                targets: value.targets.map(serializeActivityTarget),
                intervalUnit: value.intervalUnit,
                intervalCount: value.intervalCount,
            },
            { onSuccess: () => cancelNewRow(key) },
        );
    };

    const saveTouchpoint = (id: string, value: EditableTouchpoint) => {
        if (value.targets.length === 0) {
            return;
        }
        handleEvent("marketing-plan-touchpoint-update");
        updateTouchpoint.mutate({
            regionId,
            id: id as PlaybookTouchpointId,
            targets: value.targets.map(serializeActivityTarget),
            intervalUnit: value.intervalUnit,
            intervalCount: value.intervalCount,
        });
    };

    const deleteTouchpointById = (id: string) => {
        handleEvent("marketing-plan-touchpoint-delete");
        deleteTouchpoint.mutate({ regionId, id: id as PlaybookTouchpointId });
    };

    const startCustomizing = () => {
        handleEvent("marketing-plan-customize");
        customize.mutate({ regionId });
    };

    const revertToAgencyPlan = async () => {
        const confirmed = await fancyConfirm(
            "Revert to agency plan?",
            "This discards your personalized activities and puts you back on your agency's marketing plan.",
            "Yes, revert",
            "Cancel",
        );
        if (!confirmed) {
            return;
        }
        handleEvent("marketing-plan-revert");
        reset.mutate({ regionId });
    };

    if (myPlaybookQuery.isLoading) {
        return (
            <div className="flex justify-center p-4">
                <Spinner />
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-gray-600">
                    {isPersonal
                        ? "You've personalized your marketing plan. These cadences drive your agenda."
                        : hasAgencyPlaybook
                          ? "You're following your agency's marketing plan."
                          : "Your agency hasn't set up a marketing plan yet."}
                </p>
                {isPersonal ? (
                    <SecondaryButton onClick={revertToAgencyPlan}>
                        Revert to agency plan
                    </SecondaryButton>
                ) : (
                    <PrimaryButton onClick={startCustomizing}>
                        {hasAgencyPlaybook ? "Customize my plan" : "Create my plan"}
                    </PrimaryButton>
                )}
            </div>
            <div className="space-y-3">
                {tiers.map(tier => {
                    const tierItemId = tier.id as CustomListItemId;
                    const tierTouchpoints = touchpointsByTier.get(tier.id) ?? [];
                    const tierProjection = projectionByTier.get(tierItemId);
                    const sourceCount = tierProjection?.sourceCount ?? 0;
                    return (
                        <div
                            key={tier.id}
                            className="space-y-3 rounded-lg border border-gray-200 p-4">
                            <div className="flex items-center justify-between gap-2">
                                <div className="text-sm font-medium text-gray-800">
                                    {tier.name || "Tier"}
                                </div>
                                <span className="text-xs text-gray-500">
                                    {sourceCount} referral{" "}
                                    {sourceCount === 1 ? "source" : "sources"}
                                </span>
                            </div>
                            {isPersonal ? (
                                <TierTouchpointsEditor
                                    touchpoints={tierTouchpoints}
                                    newRows={newRows
                                        .filter(row => row.tierListItemId === tier.id)
                                        .map(row => ({
                                            key: row.key,
                                            initial: row.initial,
                                        }))}
                                    tierKey={tier.key}
                                    disabled={false}
                                    onAddRow={initial => addRow(tierItemId, initial)}
                                    onSaveTouchpoint={saveTouchpoint}
                                    onDeleteTouchpoint={deleteTouchpointById}
                                    onCreateTouchpoint={createTouchpointFromRow}
                                    onCancelNewRow={cancelNewRow}
                                />
                            ) : tierTouchpoints.length === 0 ? (
                                <div className="text-sm text-gray-400">
                                    No activities scheduled.
                                </div>
                            ) : (
                                <ul className="space-y-1">
                                    {tierTouchpoints.map(touchpoint => (
                                        <li
                                            key={touchpoint.id}
                                            className="text-sm text-gray-700">
                                            <TouchpointSummary touchpoint={touchpoint} />
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {tierProjection &&
                            tierProjection.touchpointVolumeGroups.some(
                                group => group.volume > 0,
                            ) ? (
                                <div className="space-y-0.5 text-xs text-gray-500">
                                    <div>
                                        Estimated rest of this month:{" "}
                                        {formatTierVolume(
                                            scaleTierVolumeGroups(
                                                tierProjection.touchpointVolumeGroups,
                                                remainingFraction,
                                            ),
                                        )}
                                    </div>
                                    <div>
                                        Estimated total this month:{" "}
                                        {formatTierVolume(
                                            tierProjection.touchpointVolumeGroups,
                                        )}
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
